import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatDistance, fromUnixTime } from 'date-fns';

/**
 * SaveStatus Component
 *
 * Displays the save state of the draft form:
 * - "Saving is pending..." (when unsaved changes detected)
 * - "Auto-saving..." (during save operation)
 * - "Saved X ago" with checkmark (after successful save)
 * - "Auto-saving failed" with warning icon in red (on error)
 *
 * The component uses timeNow to update the "ago" display every second without
 * requiring new renders from parent components.
 *
 * Memoized to prevent unnecessary re-renders from parent state changes.
 */
function SaveStatus({
  lastSavedAt = null,
  savingError = null,
  savingState,
  timeNow,
  version,
  lastSavedVersion,
  requestStatus,
}) {
  // Format the "X ago" string, updating on each timeNow tick
  const timeAgoString = useMemo(() => {
    if (!lastSavedAt) return '';
    try {
      return formatDistance(fromUnixTime(timeNow), fromUnixTime(lastSavedAt), { addSuffix: false });
    } catch (error) {
      return '';
    }
  }, [lastSavedAt, timeNow]); // Depend on timeNow to update every second

  // For live requests (scheduled/posted) with unsaved changes, show persistent message
  // This takes priority over all auto-save states since auto-save is disabled
  const isLiveRequest = requestStatus === 'scheduled' || requestStatus === 'posted';
  const hasUnsavedChanges = version > lastSavedVersion;

  if (isLiveRequest && hasUnsavedChanges) {
    return (
      <div className="d-flex align-items-center text-secondary" style={{ gap: '8px' }}>
        <span style={{ fontSize: '14px' }}>Unsaved changes</span>
      </div>
    );
  }

  // Render nothing if idle and no last save
  if (savingState === 'idle' && !lastSavedAt && !savingError) {
    return null;
  }

  // Error state takes precedence
  if (savingError) {
    return (
      <div className="d-flex align-items-center text-danger" style={{ gap: '8px' }}>
        <i className="fa fa-triangle-exclamation" />
        <span style={{ fontSize: '14px' }}>{savingError === 'manual' ? 'Saving failed' : 'Auto-saving failed'}</span>
      </div>
    );
  }

  // Pending state
  if (savingState === 'pending') {
    return (
      <div className="d-flex align-items-center text-secondary" style={{ gap: '8px' }}>
        <span style={{ fontSize: '14px' }}>Saving...</span>
      </div>
    );
  }

  // Saving state
  if (savingState === 'saving') {
    return (
      <div className="d-flex align-items-center text-secondary" style={{ gap: '8px' }}>
        <span style={{ fontSize: '14px' }}>Auto-saving...</span>
      </div>
    );
  }

  // Idle state with last saved timestamp
  if (savingState === 'idle' && lastSavedAt && timeAgoString) {
    return (
      <div className="d-flex align-items-center text-secondary" style={{ gap: '8px' }}>
        <i className="fa fa-check" />
        <span style={{ fontSize: '14px' }}>Saved {timeAgoString} ago</span>
      </div>
    );
  }

  return null;
}

SaveStatus.propTypes = {
  savingState: PropTypes.oneOf(['idle', 'pending', 'saving']).isRequired,
  lastSavedAt: PropTypes.number,
  timeNow: PropTypes.number.isRequired,
  savingError: PropTypes.oneOf([null, 'auto', 'manual']),
  version: PropTypes.number,
  lastSavedVersion: PropTypes.number,
  requestStatus: PropTypes.oneOf(['draft', 'scheduled', 'posted']),
};

SaveStatus.defaultProps = {
  lastSavedAt: null,
  savingError: null,
  version: 0,
  lastSavedVersion: 0,
  requestStatus: null,
};

export default SaveStatus;
